import {useEffect, useState} from 'react';
import {UseFormRegister, UseFormSetValue, useForm} from 'react-hook-form';
import {IFormInput, RoundedModal} from '..';
import {format} from 'date-fns';
import clsx from 'clsx';
import {ChevronLeft, ChevronRight} from 'react-feather';
import Carousel from 'nuka-carousel';
import {Image} from '@shopify/hydrogen';
import {Calendar} from 'react-calendar';

const emailRegexPattern = /^\S+@\S+\.\S+$/;

const cardImages = [
  '/images/gift-cards/01.png',
  '/images/gift-cards/02.png',
  '/images/gift-cards/03.png',
  '/images/gift-cards/04.png',
  '/images/gift-cards/05.png',
  '/images/gift-cards/06.png',
  '/images/gift-cards/07.png',
];

interface Props {
  giftDate: Date;
  register: UseFormRegister<IFormInput>;
  setValue: UseFormSetValue<IFormInput>;
  setGiftDate: (date: Date) => void;
  setSelectedCard: (value: string) => void;
}

export function ProductPersonalizeGift({
  register, 
  setValue, 
  giftDate, 
  setGiftDate, 
  setSelectedCard
}: Props) {
  
  const [slideIndex, setSlideIndex] = useState(0);
  const [isChooseDate, setIsChooseDate] = useState(false);
  const [editDate, setEditDate] = useState<Date>(new Date());

  useEffect(() => {
    setSelectedCard(cardImages[slideIndex]);
  }, [slideIndex]);

  return (
    <div>
      <div className="text-[18px] font-semibold leading-[18px] text-acai-900">
        Gift a <span className="font-serif italic">Refresh!</span>
      </div>
      <div className="mt-4 text-[14px] leading-[132%] text-acai-900">
        Your recipient will receive an email with redemption instructions.
      </div>
      <div className="my-6 flex flex-col">
        <div className="flex flex-col">
          <div className="text-[14px] font-bold text-acai-900">
            Recipient Details:
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2">
            <input
              className="rounded-lg border-[0.5px] border-[#553C38] px-[20px] py-[9px] text-[14px] text-acai-900 focus:outline-none"
              type="text"
              placeholder="Name"
              required
              {...register('to_name', {required: 'required'})}
            />
            <input
              className="rounded-lg border-[0.5px] border-[#553C38] px-[20px] py-[9px] text-[14px] text-acai-900 focus:outline-none"
              type="email"
              placeholder="Email"
              {...register('email', {
                required: 'Email is required',
                validate: {
                  matchPattern: (v) =>
                    emailRegexPattern.test(v.toString()) ||
                    'Email address must be a valid address',
                },
              })}
            />
          </div>
        </div>
        <div className="my-[16px] h-[1px] w-full bg-clay-100" />
        <div className="flex flex-col">
          <div className="text-[14px] font-bold text-acai-900">
            Sender Details:
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2">
            <input
              className="rounded-lg border-[0.5px] border-[#553C38] px-[20px] py-[9px] text-[14px] text-acai-900 focus:outline-none"
              type="text"
              placeholder="Name"
              {...register('from_name', {required: true})}
            />
            <input
              className="rounded-lg border-[0.5px] border-[#553C38] px-[20px] py-[9px] text-[14px] text-acai-900 focus:outline-none"
              type="email"
              placeholder="Email"
              {...register('from_email', {
                required: 'Email is required',
                validate: {
                  matchPattern: (v) =>
                    emailRegexPattern.test(v.toString()) ||
                    'Email address must be a valid address',
                },
              })}
            />
          </div>
        </div>
        <div className="my-[16px] h-[1px] w-full bg-clay-100" />
        <div className="flex flex-col">
          <div className="text-[14px] font-bold text-acai-900">
            Email Send Date:
          </div>
          <input
            className="mt-4 rounded-lg border-[0.5px] border-[#553C38] px-[20px] py-[9px] text-[14px] text-acai-900 focus:outline-none"
            type="text"
            placeholder="Schedule send date"
            onClick={() => setIsChooseDate(true)}
            //Keep it local here
            value={format(giftDate, 'EEEE, d MMMM yyyy')}
            {...register('date')}
          />
        </div>
        <div className="my-[16px] h-[1px] w-full bg-clay-100" />
        <div className="flex flex-col">
          <div className="text-[14px] font-bold text-acai-900">Message:</div>
          <textarea
            className="mt-4 rounded-lg border-[0.5px] border-[#553C38] !px-[20px] !py-[9px] text-[14px] text-acai-900 focus:outline-none"
            rows={3}
            placeholder="Hope you enjoy your Refresh :)"
            defaultValue=""
            {...register('message')}
          ></textarea>
        </div>
        <div className="my-[16px] h-[1px] w-full bg-clay-100" />
        <div className="flex flex-col">
          <div className="text-[14px] font-bold text-acai-900">
            Select a Card:
          </div>
          <div className="mt-4 flex w-full items-center justify-center gap-[10px]">
            <div>
              <button
                onClick={() =>
                  setSlideIndex((prev) =>
                    prev > 0 ? prev - 1 : cardImages.length - 1,
                  )
                }
                className={clsx(
                  'flex items-center justify-center rounded-full border border-acai-900 bg-transparent p-1',
                )}
              >
                <ChevronLeft stroke="#593B37" width={24} height={24} />
              </button>
            </div>
            <Carousel
              className="!w-[180px]"
              wrapAround={true}
              slideIndex={slideIndex}
              slidesToShow={1}
              cellSpacing={2}
              withoutControls={true}
            >
              {cardImages.map((cardImage, index) => {
                return (
                  <Image
                    // eslint-disable-next-line react/no-array-index-key
                    key={`card-image-${index}`}
                    className="mx-auto aspect-square cursor-pointer rounded-lg"
                    src={cardImage}
                    alt="..."
                    width={180}
                    height={180}
                  />
                );
              })}
            </Carousel>
            <div>
              <button
                onClick={() =>
                  setSlideIndex((prev) =>
                    prev < cardImages.length - 1 ? prev + 1 : 0,
                  )
                }
                className={clsx(
                  'flex items-center justify-center rounded-full border border-acai-900 bg-transparent p-1',
                )}
              >
                <ChevronRight stroke="#593B37" width={24} height={24} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <RoundedModal
        isOpen={isChooseDate}
        onClose={() => {
          setIsChooseDate(false);
          setEditDate(giftDate);
        }}
        onConfirm={() => {
          setIsChooseDate(false);
          setGiftDate(editDate);
          setValue('date', format(editDate, 'dd-MM-yyyy'));
        }}
        confirmButtonText="Select"
      >
        <div className="px-6 py-6">
          <Calendar
            className="tfr"
            value={editDate}
            minDate={new Date()}
            onChange={(value: Date) => {
              setEditDate(value);
            }}
          />
        </div>
      </RoundedModal>
    </div>
  );
}
